// src/components/FormLists/FormListsDependencyCell.jsx

import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import {
  canConfigureFormListDependenciesForGroup,
  getFormListsDependencyDisabledReason,
} from './formListsGovernanceUtils';

import {
  normalizeString,
} from '../Spreadsheet/groupedSpreadsheetUtils';

/**
 * FormListsDependencyCell
 * -----------------------------------------------------------------------------
 * Custom Form Lists cell renderer for the required "Dep" column.
 *
 * PURPOSE
 *  • Keep the shared Spreadsheet/ engine domain-free
 *  • Render dependency state for a specific option row
 *  • Preserve the existing Form Lists dependency side-panel contract:
 *      - fieldId
 *      - optionsSetUFI
 *      - optionId
 *  • Keep dependency persistence in DropdownDependenciesContext
 *
 * IMPORTANT
 *  • This component does NOT read React context directly.
 *  • This component does NOT save dependencies.
 *  • This component does NOT mutate dropdownOptions.
 *  • This component does NOT know about Firestore.
 *  • This component does NOT store dependencies in row data.
 *
 * SCHEMA ALIGNMENT
 *  • The dependency graph remains keyed by:
 *      dropdownDependencies[optionsSetUFI][optionId].dependentOptionIds
 *  • The row provides optionId.
 *  • The group provides fieldId, optionsSetUFI, fieldType, and governance flags.
 *
 * ENTERPRISE UX RULES
 *  • Blank rows do not show dependency actions.
 *  • Rows without optionId do not open dependency settings.
 *  • Org-managed dependency editing remains available in Lists Lab because
 *    dependencies are form-specific in the current tested architecture.
 *  • Multi-select trigger dependency editing stays disabled for now to avoid
 *    changing current dependency behavior during the spreadsheet refactor.
 * -----------------------------------------------------------------------------
 */

const isMeaningfulOptionRow = (row = {}) => {
  const label = normalizeString(row?.label, '');
  return label.length > 0;
};

const getDependencyCount = ({
  dependencyFlags = {},
  getDependenciesForOption,
  optionsSetUFI,
  optionId,
} = {}) => {
  if (!optionId || !optionsSetUFI) {
    return 0;
  }

  if (
    dependencyFlags &&
    Object.prototype.hasOwnProperty.call(dependencyFlags, optionId)
  ) {
    return dependencyFlags[optionId] ? 1 : 0;
  }

  if (typeof getDependenciesForOption !== 'function') {
    return 0;
  }

  const dependencies = getDependenciesForOption(optionsSetUFI, optionId);

  return Array.isArray(dependencies) ? dependencies.length : 0;
};

const buildDependencyActionLabel = ({
  hasDependencies,
  canOpenDependencies,
  isBlankRow,
  optionId,
  isMultiSelectTrigger,
  disabledReason,
} = {}) => {
  if (isBlankRow) {
    return '';
  }

  if (!optionId) {
    return 'Save or apply this option before configuring dependencies.';
  }

  if (isMultiSelectTrigger) {
    return 'Dependencies are currently configured from single-select trigger options.';
  }

  if (!canOpenDependencies && disabledReason) {
    return disabledReason;
  }

  if (!canOpenDependencies) {
    return 'Dependency editing is unavailable for this option.';
  }

  if (hasDependencies) {
    return 'Edit dependencies for this option.';
  }

  return 'Add dependencies for this option.';
};

const FormListsDependencyCell = memo(({
  group,
  groupId,
  row,
  rowId,
  rowIndex,
  selected = false,
  readOnly = false,
  disabled = false,
  dependencyFlags = {},
  getDependenciesForOption,
  onOpenDependencySettings,
}) => {
  const fieldId = normalizeString(group?.fieldId, '');
  const optionsSetUFI = normalizeString(group?.optionsSetUFI, '');
  const optionId = normalizeString(row?.optionId, '');
  const fieldType = normalizeString(group?.fieldType, '');
  const isBlankRow = !isMeaningfulOptionRow(row);

  const isMultiSelectTrigger = fieldType === 'multi-select';

  const hasDependencies = useMemo(() => {
    return getDependencyCount({
      dependencyFlags,
      getDependenciesForOption,
      optionsSetUFI,
      optionId,
    }) > 0;
  }, [
    dependencyFlags,
    getDependenciesForOption,
    optionsSetUFI,
    optionId,
  ]);

  const dependenciesAllowedForGroup = useMemo(() => (
    canConfigureFormListDependenciesForGroup(group)
  ), [
    group,
  ]);

  const disabledReason = useMemo(() => {
    if (disabled) {
      return 'This dependency cell is disabled.';
    }

    return getFormListsDependencyDisabledReason(group);
  }, [
    disabled,
    group,
  ]);

  /**
   * Dependency action availability
   * ---------------------------------------------------------------------------
   * Dependencies are form-specific, so SmartList/org-managed groups are allowed
   * to participate in dependency rules even when their option values are
   * read-only in the spreadsheet.
   *
   * IMPORTANT
   *  • Do not use the cell readOnly prop to block this action.
   *  • The Option / Hint cells are centrally governed by SmartLists.
   *  • The dependency relationship belongs to the current form.
   *  • Multi-select trigger dependency editing remains disabled for now to
   *    preserve the current tested dependency behavior.
   */
  const canOpenDependencies = Boolean(
    !isBlankRow &&
      optionId &&
      fieldId &&
      optionsSetUFI &&
      !disabled &&
      dependenciesAllowedForGroup &&
      !isMultiSelectTrigger &&
      typeof onOpenDependencySettings === 'function',
  );

  const dependencyActionLabel = buildDependencyActionLabel({
    hasDependencies,
    canOpenDependencies,
    isBlankRow,
    optionId,
    isMultiSelectTrigger,
    disabledReason,
  });

  const handleDependencyMouseDown = useCallback((event) => {
    // Prevent the shared spreadsheet cell from treating the icon press as a
    // normal cell-selection mouse down. The dependency icon is an action inside
    // a custom cell, not a text-edit surface.
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleOpenDependencySettings = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!canOpenDependencies) {
      return;
    }

    onOpenDependencySettings({
      group,
      groupId,
      fieldId,
      optionsSetUFI,
      optionId,
      row,
      rowId,
      rowIndex,
      fieldType,
    });
  }, [
    canOpenDependencies,
    onOpenDependencySettings,
    group,
    groupId,
    fieldId,
    optionsSetUFI,
    optionId,
    row,
    rowId,
    rowIndex,
    fieldType,
  ]);

  if (isBlankRow) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
    );
  }

  const iconColor = hasDependencies ? 'primary.main' : 'text.disabled';

  const icon = isMultiSelectTrigger || !dependenciesAllowedForGroup
    ? (
        <LockOutlinedIcon
          fontSize="inherit"
          sx={{
            color: 'text.disabled',
          }}
        />
      )
    : (
        <AccountTreeIcon
          fontSize="inherit"
          sx={{
            color: iconColor,
          }}
        />
      );

  return (
    <span
      style={{
        width: '100%',
        height: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconButton
        size="small"
        disabled={!canOpenDependencies}
        onMouseDown={handleDependencyMouseDown}
        onClick={handleOpenDependencySettings}
        aria-label={dependencyActionLabel || 'Dependency cell'}
        sx={{
          width: 24,
          height: 24,
          color: iconColor,
          opacity: canOpenDependencies ? 1 : 0.55,
          borderRadius: 1,
          backgroundColor:
            selected && hasDependencies
              ? 'action.selected'
              : 'transparent',
          '&:hover': {
            backgroundColor: canOpenDependencies
              ? 'action.hover'
              : 'transparent',
          },
        }}
      >
        {icon}
      </IconButton>
    </span>
  );
});

FormListsDependencyCell.displayName = 'FormListsDependencyCell';

FormListsDependencyCell.propTypes = {
  group: PropTypes.object.isRequired,
  groupId: PropTypes.string.isRequired,
  row: PropTypes.object.isRequired,
  rowId: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  dependencyFlags: PropTypes.object,
  getDependenciesForOption: PropTypes.func,
  onOpenDependencySettings: PropTypes.func,
};

export default FormListsDependencyCell;